import { useState, FormEvent } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEO from '../../components/feature/SEO';

const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

const assessmentJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Free Home Safety Assessment - SafeStay Solutions Beverly MA',
    description:
      'Schedule your free home safety assessment with SafeStay Solutions in Beverly, MA. No obligation. Our certified specialists evaluate your home and provide personalized accessibility recommendations.',
    url: `${siteUrl}/free-assessment`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: 'Free Assessment', item: `${siteUrl}/free-assessment` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Free Home Safety Assessment',
    description: 'A complimentary room-by-room evaluation of your home\'s safety and accessibility needs by certified specialists.',
    provider: {
      '@type': 'LocalBusiness',
      name: 'SafeStay Solutions',
      telephone: '+16175396650',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Beverly',
        addressRegion: 'MA',
        addressCountry: 'US',
      },
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    areaServed: {
      '@type': 'State',
      name: 'Massachusetts',
    },
  },
];

export default function FreeAssessmentPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cityState: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('cityState', formData.cityState);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d62cvhs3ij519rauvnpg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', cityState: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Free Home Safety Assessment Beverly MA - SafeStay Solutions"
        description="Schedule your free, no-obligation home safety assessment in Beverly, MA. SafeStay Solutions certified specialists evaluate your home and provide personalized accessibility recommendations. Call (617) 539-6650."
        keywords="free home safety assessment Beverly MA, free accessibility evaluation, aging in place consultation, home safety inspection North Shore"
        canonical={`${siteUrl}/free-assessment`}
        ogUrl={`${siteUrl}/free-assessment`}
        jsonLd={assessmentJsonLd}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1A2B4A] to-[#2A3B5A] pt-32 pb-16 sm:pb-20 md:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-block bg-[#D4AF37]/20 text-[#D4AF37] px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-6 sm:mb-8">
              NO OBLIGATION • 100% FREE
            </div>
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight mb-6 sm:mb-8">
              Schedule Your Free<br />Home Safety Assessment
            </h1>
            <p className="text-white/90 text-lg sm:text-xl md:text-2xl font-light max-w-3xl mx-auto mb-8 sm:mb-10">
              Our certified specialists will evaluate your home and provide personalized recommendations to enhance safety and accessibility—completely free with no obligation.
            </p>

            {/* Trust Signals */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-4xl mx-auto mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-white text-3xl"></i>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Licensed & Insured</h3>
                <p className="text-white/80 text-sm">Fully certified professionals you can trust</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-white text-3xl"></i>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Fast Response</h3>
                <p className="text-white/80 text-sm">We'll contact you within 24 hours</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-star-line text-white text-3xl"></i>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Expert Guidance</h3>
                <p className="text-white/80 text-sm">Personalized safety recommendations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-xl">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="text-[#1A2B4A] text-3xl sm:text-4xl font-bold mb-4">
                Get Started Today
              </h2>
              <p className="text-gray-600 text-base sm:text-lg">
                Fill out the form below and we'll reach out to schedule your free assessment at a time that works for you.
              </p>
            </div>

            <form id="assessment-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                <div>
                  <label htmlFor="name" className="block text-[#1A2B4A] font-semibold mb-2 text-sm sm:text-base">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm sm:text-base"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-[#1A2B4A] font-semibold mb-2 text-sm sm:text-base">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm sm:text-base"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                <div>
                  <label htmlFor="phone" className="block text-[#1A2B4A] font-semibold mb-2 text-sm sm:text-base">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm sm:text-base"
                    placeholder="(978) 555-0123"
                  />
                </div>
                <div>
                  <label htmlFor="cityState" className="block text-[#1A2B4A] font-semibold mb-2 text-sm sm:text-base">
                    City, State *
                  </label>
                  <input
                    type="text"
                    id="cityState"
                    name="cityState"
                    required
                    value={formData.cityState}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm sm:text-base"
                    placeholder="Boston, MA"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-[#1A2B4A] font-semibold mb-2 text-sm sm:text-base">
                  Tell Us About Your Needs
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm sm:text-base resize-none"
                  placeholder="Please describe your accessibility needs or any specific concerns..."
                ></textarea>
                <div className="text-right text-xs sm:text-sm text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#D4AF37] hover:bg-[#C4A137] text-[#1A2B4A] font-bold text-base sm:text-lg py-3 sm:py-4 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Request Free Assessment'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-500 text-green-700 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-center text-sm sm:text-base">
                  <strong>Thank you!</strong> We'll contact you within 24 hours to schedule your free assessment.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-500 text-red-700 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-center text-sm sm:text-base">
                  <strong>Oops!</strong> Something went wrong. Please call us at (617) 539-6650.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-[#1A2B4A] text-3xl sm:text-4xl font-bold mb-4">
              Prefer to Talk Directly?
            </h2>
            <p className="text-gray-600 text-base sm:text-lg">
              We're here to answer your questions and discuss your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <a
              href="tel:6175396650"
              className="group bg-[#1A2B4A] hover:bg-[#2A3B5A] rounded-2xl p-8 text-center transition-colors cursor-pointer"
            >
              <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-white text-3xl"></i>
              </div>
              <h3 className="text-white font-bold text-xl mb-2">Call Us</h3>
              <p className="text-white/80 text-lg font-semibold">(617) 539-6650</p>
              <p className="text-white/60 text-sm mt-2">Mon-Fri: 8am-6pm</p>
            </a>

            <a
              href="mailto:safestayma@gmail.com"
              className="group bg-[#1A2B4A] hover:bg-[#2A3B5A] rounded-2xl p-8 text-center transition-colors cursor-pointer"
            >
              <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-white text-3xl"></i>
              </div>
              <h3 className="text-white font-bold text-xl mb-2">Email Us</h3>
              <p className="text-white/80 text-lg font-semibold break-all">safestayma@gmail.com</p>
              <p className="text-white/60 text-sm mt-2">We respond within 24 hours</p>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
